class BlogLoadMore {
  constructor() {
    // Prevent multiple instances
    if (window.blogLoadMoreInstance) {
      return;
    }

    this.container = document.querySelector('.blog-articles');
    this.loadMoreButton = document.querySelector('.blog-load-more-btn');
    this.paginationInfo = document.querySelector('.blog-pagination-info');
    this.allArticlesContainer = document.querySelector('.blog-articles-all');
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMorePages = true;
    this.currentLocale = document.documentElement.lang || 'en';
    this.articlesPerPage = 6;
    this.totalArticlesLoaded = 0;
    this.allArticles = [];
    this.filteredArticles = [];

    // Store instance globally to prevent duplicates
    window.blogLoadMoreInstance = this;

    this.init();
  }

  init() {
    if (!this.container) {
      return;
    }

    // Count initial articles
    this.totalArticlesLoaded = this.container.children.length;

    // Get all articles from the hidden container
    if (this.allArticlesContainer) {
      this.allArticles = Array.from(this.allArticlesContainer.querySelectorAll('.blog-articles__article'));

      // Filter articles by locale
      this.filteredArticles = this.allArticles.filter((article) => {
        const shouldShow = this.shouldShowArticle(article);
        return shouldShow;
      });
    }

    // Check pagination info for filtered articles
    if (this.paginationInfo) {
      const totalFilteredArticles = parseInt(this.paginationInfo.dataset.totalFilteredArticles);
      const articlesPerPage = parseInt(this.paginationInfo.dataset.articlesPerPage) || 6;

      this.articlesPerPage = articlesPerPage;
      this.hasMorePages = this.totalArticlesLoaded < totalFilteredArticles;
    } else {
      this.hasMorePages = false;
    }

    // Set up button listener
    this.setupButtonListener();
  }

  setupButtonListener() {
    if (!this.loadMoreButton) {
      return;
    }

    // Remove any existing listeners to prevent duplicates
    this.loadMoreButton.removeEventListener('click', this.handleButtonClick);

    // Create bound event handler
    this.handleButtonClick = (event) => {
      if (!this.isLoading && this.hasMorePages) {
        this.loadMoreArticles();
      }
    };

    this.loadMoreButton.addEventListener('click', this.handleButtonClick);
  }

  // Check if an article should be shown based on locale
  shouldShowArticle(articleElement) {
    // Look for the metafield data in the article element
    const metafieldName = `show_${this.currentLocale}`;

    // Check if the article has the metafield data attribute
    const metafieldValue = articleElement.getAttribute(`data-${metafieldName}`);

    if (metafieldValue !== null) {
      return metafieldValue === 'true';
    }

    // Fallback: check if the article has the locale class or data attribute
    const hasLocaleClass = articleElement.classList.contains(`locale-${this.currentLocale}`);
    const hasLocaleData = articleElement.getAttribute('data-locale') === this.currentLocale;

    return hasLocaleClass || hasLocaleData;
  }

  // Get localized button text
  getLocalizedButtonText(type) {
    const texts = {
      loadMore: {
        fr: "Charger plus d'articles",
        en: 'Load More Articles',
        ar: 'تحميل المزيد من المقالات',
      },
      loading: {
        fr: 'Chargement...',
        en: 'Loading...',
        ar: 'جاري التحميل...',
      },
      noMore: {
        fr: 'Aucun autre article',
        en: 'No more articles',
        ar: 'لا توجد مقالات أخرى',
      },
      error: {
        fr: 'Erreur de chargement. Réessayez.',
        en: 'Error loading articles. Try again.',
        ar: 'خطأ في تحميل المقالات. حاول مرة أخرى.',
      },
    };

    return texts[type][this.currentLocale] || texts[type].en;
  }

  // Update button text based on locale
  updateButtonText(text) {
    if (!this.loadMoreButton) return;

    // Hide all text spans
    const textSpans = this.loadMoreButton.querySelectorAll(
      '.blog-load-more-text-fr, .blog-load-more-text-en, .blog-load-more-text-ar'
    );
    textSpans.forEach((span) => (span.style.display = 'none'));

    // Show the appropriate text span for current locale
    const currentTextSpan = this.loadMoreButton.querySelector(`.blog-load-more-text-${this.currentLocale}`);
    if (currentTextSpan) {
      currentTextSpan.style.display = 'inline';
      currentTextSpan.textContent = text;
    } else {
      // Fallback: set text directly on button
      this.loadMoreButton.textContent = text;
    }
  }

  async loadMoreArticles() {
    if (this.isLoading || !this.hasMorePages) {
      return;
    }

    this.isLoading = true;
    this.loadMoreButton.classList.add('loading');
    this.updateButtonText(this.getLocalizedButtonText('loading'));

    try {
      // Calculate how many articles to load
      const articlesToLoad = Math.min(this.articlesPerPage, this.filteredArticles.length - this.totalArticlesLoaded);

      if (articlesToLoad > 0) {
        // Get the next batch of articles
        const startIndex = this.totalArticlesLoaded;
        const endIndex = startIndex + articlesToLoad;
        const articlesToAdd = this.filteredArticles.slice(startIndex, endIndex);

        // Add articles to the container
        articlesToAdd.forEach((article, index) => {
          // Clone the article and add animation classes
          const clonedArticle = article.cloneNode(true);
          clonedArticle.classList.add('animate--slide-in');
          clonedArticle.style.setProperty('--animation-order', this.container.children.length + index + 1);

          this.container.appendChild(clonedArticle);
        });

        // Update counters
        this.totalArticlesLoaded += articlesToAdd.length;
        this.currentPage += 1;

        // Check if there are more articles to load
        this.hasMorePages = this.totalArticlesLoaded < this.filteredArticles.length;

        if (!this.hasMorePages) {
          this.updateButtonText(this.getLocalizedButtonText('noMore'));
          this.loadMoreButton.disabled = true;
        } else {
          // Update button text
          this.updateButtonText(this.getLocalizedButtonText('loadMore'));
        }

        // Trigger animations for new articles
        if (window.shopify && window.shopify.designMode) {
          // In theme editor, trigger animations manually
          setTimeout(() => {
            articlesToAdd.forEach((article) => {
              article.classList.add('animate--slide-in');
            });
          }, 100);
        }
      } else {
        // No more articles
        this.hasMorePages = false;
        this.updateButtonText(this.getLocalizedButtonText('noMore'));
        this.loadMoreButton.disabled = true;
      }
    } catch (error) {
      this.updateButtonText(this.getLocalizedButtonText('error'));
    } finally {
      this.isLoading = false;
      this.loadMoreButton.classList.remove('loading');
    }
  }
}

// Initialize load more when DOM is ready (only once)
document.addEventListener('DOMContentLoaded', () => {
  new BlogLoadMore();
});

// Also initialize if the script is loaded after DOM is ready (only once)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new BlogLoadMore();
  });
} else {
  new BlogLoadMore();
}
